import { type Locale, packShapeFor, profileOf } from './alphabet';
import { fnv1a, toHex8 } from './hash';

export interface Question {
  readonly id: string; // 'c001' 형식 고정. 팩을 넘어 유일, 발행 후 불변
  readonly q: string; // 문제문 6~40자
  readonly a: string; // 정답 — 완성형 음절 2~4자, 중복 음절 없음
  readonly d: readonly string[]; // authored 오답 >= 5
  readonly tags?: readonly string[];
  /** 추론형 팩 전용 연상 힌트 2~3개 — hints[0] 은 항상 표시, 나머지는 분노 단계로 공개(RULES §1.8 HINTS_SPEC) */
  readonly hints?: readonly string[];
}

export type PackKind = 'joke' | 'inference';

export interface ContentPack {
  readonly id: string;
  readonly version: string;
  readonly kind?: PackKind; // 없으면 'joke'
  /** 로케일 — 알파벳 모듈·정답 길이 프로필을 정한다(RULES §1.17). 없으면 'ko'. 해시 밖 */
  readonly locale?: Locale;
  readonly questions: readonly Question[];
}

export const packKindOf = (p: ContentPack): PackKind => p.kind ?? 'joke';

export const PACK_SIZE = 24;
export const PACK_SHAPE: Readonly<Record<number, number>> = { 2: 6, 3: 12, 4: 6 };
export const ANSWER_MIN_LEN = 2;
export const ANSWER_MAX_LEN = 4;
export const DISTRACTORS_MIN = 5;
export const LADDER_MIN_LENGTH = 24;
export const TRAP_MIN_PER_SLOT = 4;

const ID_RE = /^c\d{3}$/;

export interface PackIssue {
  readonly rule: number;
  readonly id: string;
  readonly message: string;
}

/** 부팅·테스트에서 항상 돈다. 위반은 throw. 목록만 필요하면 `packIssues`. */
export function validatePack(p: ContentPack): void {
  const issues = packIssues(p);
  if (issues.length > 0) {
    const head = issues.slice(0, 8).map((i) => `  [규칙 ${i.rule}] ${i.id}: ${i.message}`);
    const more = issues.length > 8 ? `\n  ...외 ${issues.length - 8}건` : '';
    throw new Error(`validatePack 실패 (${issues.length}건)\n${head.join('\n')}${more}`);
  }
}

/**
 * 한 문제의 규칙(4~14) — 팩 단위 규칙(1 문항 수 · 2 규격 · 3 id 중복)은 여기 없다.
 * 투고 화면·콘솔 승인 큐가 문제 하나를 볼 때 쓰고, `packIssues` 도 이 함수를 문항마다 부른다(규칙의 단일 출처).
 */
export function questionRuleIssues(
  q: Question,
  ctx: { readonly kind?: PackKind | undefined; readonly locale?: Locale | undefined },
): PackIssue[] {
  const issues: PackIssue[] = [];
  const add = (rule: number, id: string, message: string): void => {
    issues.push({ rule, id, message });
  };
  const prof = profileOf(ctx);
  const ab = prof.alphabet;
  const inference = (ctx.kind ?? 'joke') === 'inference';

  if (!ID_RE.test(q.id)) add(3, q.id, 'id 형식 위반 (/^c\\d{3}$/)');
  if (!prof.answerRe.test(q.a)) {
    add(4, q.id, `정답 '${q.a}' 이 ${ab.charLabel} ${prof.minLen}~${prof.maxLen}자가 아님`);
    return issues;
  }
  const len = q.a.length;
  if (new Set(q.a.split('')).size !== len) add(5, q.id, `정답 '${q.a}' 에 중복 음절`);
  if (q.q.length < 6 || q.q.length > 40) add(6, q.id, `문제문 길이 ${q.q.length} 이 [6,40] 밖`);
  if (q.q.indexOf(q.a) >= 0) add(7, q.id, `문제문에 정답 '${q.a}' 노출`);
  if (q.d.length < DISTRACTORS_MIN)
    add(8, q.id, `authored 오답 ${q.d.length} < ${DISTRACTORS_MIN}`);
  // 규칙 14 — 추론형 힌트: 2~3개 · 각 1~12자 · 서로 다름 · 정답 부분문자열 금지. 아재개그 팩에는 없어야 한다
  if (q.hints !== undefined) {
    if (!inference) add(14, q.id, '아재개그 팩에 hints');
    if (q.hints.length < 2 || q.hints.length > 3) add(14, q.id, `hints ${q.hints.length}개 (2~3)`);
    if (new Set(q.hints).size !== q.hints.length) add(14, q.id, 'hints 중복');
    for (const h of q.hints) {
      if (h.length < 1 || h.length > 12) add(14, q.id, `hint '${h}' 길이 [1,12] 밖`);
      if (h.indexOf(q.a) >= 0) add(14, q.id, `hint '${h}' 에 정답 노출`);
    }
  } else if (inference) add(14, q.id, '추론형 문제에 hints 없음');

  const answerSet = new Set(q.a.split(''));
  for (const dch of q.d) {
    if (answerSet.has(dch)) add(9, q.id, `오답 '${dch}' 이 정답 음절과 동일`);
    let best = 0;
    for (const ach of q.a) best = Math.max(best, ab.similarity(dch, ach));
    if (best < 2) add(10, q.id, `오답 '${dch}' 의 자모 유사도 ${best} < 2`);
  }

  const ladder = ab.ladder(q.a, q.d, 0);
  if (ladder.length < ab.ladderMin) {
    add(11, q.id, `사다리 길이 ${ladder.length} < ${ab.ladderMin}`);
  }
  for (let k = 0; k < len; k++) {
    const target = q.a.charAt(k);
    let traps = 0;
    for (const c of ladder) if (ab.similarity(c, target) >= 2) traps++;
    if (traps < ab.trapMinPerSlot) {
      add(12, q.id, `'${target}' 위치 함정 후보 ${traps} < ${ab.trapMinPerSlot}`);
    }
  }
  for (const msg of ab.rareCoverIssues(q.a, q.d)) add(13, q.id, msg);
  return issues;
}

/**
 * 규칙 1~14 위반 목록. `validatePack` 은 이걸 모아 던지고, 관리자 콘솔은 편집 중에 그대로 보여준다.
 * 순서는 안정적이다(문항 순회 순 → 팩 단위 규칙 2).
 */
export function packIssues(p: ContentPack): PackIssue[] {
  const issues: PackIssue[] = [];
  const add = (rule: number, id: string, message: string): void => {
    issues.push({ rule, id, message });
  };
  const prof = profileOf(p);
  const want = packShapeFor(prof.wordLenProfile);
  const shape: Record<number, number> = {};
  for (const L of Object.keys(want)) shape[Number(L)] = 0;
  const ids = new Set<string>();

  if (p.questions.length !== PACK_SIZE) {
    add(1, p.id, `문항 수 ${p.questions.length} !== ${PACK_SIZE}`);
  }
  for (const q of p.questions) {
    if (ids.has(q.id)) add(3, q.id, 'id 중복');
    ids.add(q.id);
    const own = questionRuleIssues(q, { kind: p.kind, locale: p.locale });
    issues.push(...own);
    if (own.some((i) => i.rule === 4)) continue; // 정답 형식이 틀리면 규격 집계에서 뺀다
    shape[q.a.length] = (shape[q.a.length] ?? 0) + 1;
  }
  for (const L of Object.keys(want).map(Number)) {
    const w = want[L] as number;
    if ((shape[L] ?? 0) !== w) add(2, p.id, `${L}글자 ${shape[L] ?? 0}제 !== ${w}제`);
  }
  return issues;
}

export function packCanonical(p: ContentPack): string {
  // hints 는 있을 때만 붙인다 — 아재개그 팩(core 1.0.0)의 해시가 바뀌지 않게
  return p.questions
    .map((q) => {
      const cols = [q.id, q.q, q.a, [...q.d].sort().join('|')];
      if (q.hints !== undefined) cols.push(q.hints.join('|'));
      return cols.join('\t');
    })
    .sort()
    .join('\n');
}

export function packHash(p: ContentPack): string {
  return toHex8(fnv1a(packCanonical(p)));
}

/** 파일 순서 무관하게 만드는 안정 정렬. 배열 인덱스를 id로 쓰지 않는다. */
export function orderQuestions(p: ContentPack): readonly Question[] {
  return [...p.questions].sort((a, b) => {
    const ha = fnv1a(a.id);
    const hb = fnv1a(b.id);
    if (ha !== hb) return ha - hb;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}
